import { EntityApiDefinitionCard } from '@backstage/plugin-api-docs';
import { useEffect, useRef } from 'react';

import { useEntity } from '@backstage/plugin-catalog-react';
import { SessionStorageHelper, SessionStorageKeys } from '@internal/ui-common';

/**
 * Workaround for AsyncAPI formatting library rendering issue
 *
 * Problem: The AsyncAPI plugin's formatting library doesn't properly render example payloads and headers
 * in the API Definition view. When navigating away and back without a full page reload, the headers and
 * payloads become invisible due to a formatting issue in the library.
 *
 * Solution: Trigger a hard page reload after the component finishes loading, but only once per entity
 * per page visit to avoid constant reloads. When the user leaves the page, the reload flag is cleared so it can reload again on return.
 *
 * Flow:
 * 1. Component mounts with an AsyncAPI entity
 * 2. Watch for DOM mutations as the card renders and loads all data
 * 3. When mutations stop for 200ms (content is stable), all data is loaded
 * 4. Set a flag in sessionStorage to prevent multiple reloads
 * 5. Trigger hard reload to fix the formatting library rendering
 * 6. After reload, the flag exists so no infinite reload loop occurs
 * 7. When user leaves the component (soft navigation), cleanup removes the flag
 * 8. If user returns to this entity, the flag is gone and reload happens again
 */
function AsyncApiDefinition() {
  const { entity } = useEntity();
  const containerRef = useRef<HTMLDivElement>(null);

  const isAsyncApi = entity?.spec?.type === 'asyncapi';
  const id = `${entity?.kind}:${entity?.metadata.namespace ?? 'default'}:${
    entity?.metadata.name
  }`;
  const observerRef = useRef<MutationObserver | null>(null);
  const mutationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Effect: Watch for content stabilization and trigger hard reload
  useEffect(() => {
    if (!isAsyncApi || !containerRef.current) return;

    const storageKey = `${SessionStorageKeys.ASYNCAPI_LAST_RELOAD}:${id}`;

    if (SessionStorageHelper.getItem(storageKey)) return;

    // Monitor DOM changes as the EntityApiDefinitionCard renders
    const observer = new MutationObserver(() => {
      if (mutationTimeoutRef.current) clearTimeout(mutationTimeoutRef.current);

      mutationTimeoutRef.current = setTimeout(() => {
        SessionStorageHelper.setItem(storageKey, 'true');
        observer.disconnect();

        window.location.reload();
      }, 200);
    });

    observer.observe(containerRef.current, { childList: true, subtree: true });
    observerRef.current = observer;
  }, [isAsyncApi, entity, id]);

  // Effect: Clean up reload flag and observer when user leaves the component (soft navigation)
  useEffect(() => {
    return () => {
      if (observerRef.current) observerRef.current.disconnect();
      if (mutationTimeoutRef.current) clearTimeout(mutationTimeoutRef.current);

      SessionStorageHelper.removeItem(
        `${SessionStorageKeys.ASYNCAPI_LAST_RELOAD}:${id}`,
      );
    };
  }, [entity, id]);

  return (
    <div ref={containerRef}>
      <EntityApiDefinitionCard />
    </div>
  );
}

export { AsyncApiDefinition };
